'use client';

import { useState } from 'react';
import * as formStyles from './styles/formStyles';

// Define ContactInfo type used in this form
interface ContactInfo {
  name?: string;
  phone: string;
}

interface ContactFormProps {
  dictionary: any;
  disableSubmit?: boolean;
  onSubmit: (contactInfo: ContactInfo) => void;
  showNameInput?: boolean;
  contactPrompt?: string;
}

export default function ContactForm({
  dictionary,
  onSubmit,
  disableSubmit = false,
  showNameInput,
  contactPrompt,
}: ContactFormProps) {
  const [errors, setErrors] = useState<Partial<Record<keyof ContactInfo, string>>>({});
  // Controlled inputs for contact form
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('+32');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const contactInfo: ContactInfo = { name: showNameInput ? name : undefined, phone };
    const newErrors: Partial<Record<keyof ContactInfo, string>> = {};

    if (!contactInfo.phone) {
      newErrors.phone = dictionary?.sellingForm?.requiredField || 'This field is required';
    } else if (!/^\+?[0-9\s\-\/()]{6,}$/.test(contactInfo.phone)) {
      newErrors.phone = dictionary?.sellingForm?.invalidPhoneFormat || 'Invalid phone format';
    }

    // Add validation for name only when showNameInput is true
    if (showNameInput && !contactInfo.name) {
      newErrors.name = dictionary?.sellingForm?.requiredNameField || 'Name is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    onSubmit(contactInfo);
  };

  // Use constant for input classes
  const inputClasses = formStyles.inputElementBaseClasses;

  return (
    <>
      <form onSubmit={handleSubmit} className="w-full">
        <p
          className="text-base text-zinc-800 mb-4"
          style={{ whiteSpace: 'pre-line' }}
        >
          {contactPrompt || dictionary?.sellingForm?.contactPrompt || 'Please provide your contact details below.'}
        </p>

        {/* Contact Info Section */}
        <div className="space-y-3">
          {/* Name Input (Conditional) */}
          {showNameInput && (
            <div className="w-[75%]">
              <div className={`${formStyles.inputContainerBaseClasses}`}>
                <input
                  type="text"
                  name="name"
                  placeholder={dictionary?.sellingForm?.contactNameLabel || 'Your Name'}
                  className={`${inputClasses}`}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                />
              </div>
              {errors.name && <p className={formStyles.errorMessageClasses}>{errors.name}</p>}
            </div>
          )}

          {/* Phone */}
          <div className="w-[75%]">
            <div className={`${formStyles.inputContainerBaseClasses} relative`}>
              <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-1 pointer-events-none">
                <span className="text-base">🇧🇪</span>
                <span className="text-base text-zinc-500">+32</span>
              </div>
              <input
                type="tel"
                inputMode="tel"
                name="phone"
                placeholder={dictionary?.sellingForm?.contactPhone || 'Phone Number'}
                className={`${inputClasses} pl-[4.5rem]`}
                value={phone.substring(3)}
                onChange={(e) => {
                  const enteredValue = e.target.value;
                  const newValue = '+32' + enteredValue.replace(/[^0-9]/g, '');
                  setPhone(newValue);
                }}
                autoComplete="tel"
              />
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              {dictionary?.sellingForm?.phoneVerificationNotice || 'Note: This phone number will be verified.'}
            </p>
            {errors.phone && <p className={formStyles.errorMessageClasses}>{errors.phone}</p>}
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={disableSubmit}
          className={`${formStyles.submitButtonBaseClasses} ${disableSubmit ? formStyles.submitButtonDisabledClasses : formStyles.submitButtonHoverClasses}`}
        >
          {dictionary?.sellingForm?.showValueButton || 'Show value'}
        </button>
      </form>
    </>
  );
} 